'use client'

import Image from 'next/image'
import Link from 'next/link'
import { useEffect, useState } from 'react'
import { ChevronRight, Menu, X } from 'lucide-react'
import { cn } from '@/utils/styles/cn'
import { UrlConfig } from '@/setup/routing/routing'
import { LP_GRID } from '@/features/landing-page/v3/lp-grid'
import { PRIMARY_NAV_LINKS, SOLUTIONS_GROUPS } from '@/features/landing-page/v3/solutions-menu-config'
import { NavCtaButtons } from '@/features/landing-page/v3/nav-cta-buttons'
import { LP_V3_EASE, LP_V3_NAV_HEIGHT } from '@/features/landing-page/v3/landing-v3-nav-constants'

/**
 * Mobile nav. The menu slides down from behind the bar via a `transform` (compositor-only → smooth
 * on mobile); it is clipped to the region *below* the bar so the slide never overlaps the header.
 * The logo stays put, the Get started button fades out, and the ☰ morphs to ✕.
 *
 * The panel stays mounted (links live in the static HTML) and is `inert` while collapsed.
 */
export function NavMobileMenu() {
    const [open, setOpen] = useState(false)
    const [solutionsOpen, setSolutionsOpen] = useState(false)

    useEffect(() => {
        if (!open) return
        const previous = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        return () => {
            document.body.style.overflow = previous
        }
    }, [open])

    useEffect(() => {
        if (!open) return
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false)
        }
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [open])

    const close = () => {
        setOpen(false)
        setSolutionsOpen(false)
    }

    return (
        <>
            {/* Backdrop */}
            <div
                aria-hidden
                onClick={close}
                data-open={open}
                style={{ transitionTimingFunction: LP_V3_EASE }}
                className={cn(
                    'fixed inset-0 z-20 bg-black/25 transition-opacity duration-300 md:hidden',
                    'data-[open=false]:pointer-events-none data-[open=false]:opacity-0 data-[open=true]:opacity-100'
                )}
            />

            {/* Clip region below the bar — keeps the sliding menu from ever overlapping the header */}
            <div
                className="fixed inset-x-0 bottom-0 z-30 overflow-hidden pointer-events-none md:hidden"
                style={{ top: LP_V3_NAV_HEIGHT }}
            >
                <div
                    inert={!open}
                    style={{ transitionTimingFunction: LP_V3_EASE }}
                    className={cn(
                        'will-change-transform transition-transform duration-[360ms] motion-reduce:transition-none',
                        open ? 'translate-y-0 pointer-events-auto' : '-translate-y-full'
                    )}
                >
                    <div className="rounded-b-2xl bg-background">
                        <div className={LP_GRID.contentSection}>
                    <div className={LP_GRID.content}>
                            <nav className="flex max-h-[calc(100dvh-64px)] flex-col overflow-y-auto pb-4 pt-1 no-scrollbar">
                                <button
                                    type="button"
                                    aria-expanded={solutionsOpen}
                                    onClick={() => setSolutionsOpen((v) => !v)}
                                    className="flex w-full items-center justify-between rounded-[6px] py-3 text-[24px] font-normal leading-[1.1] text-foreground outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                                >
                                    Solutions
                                    <ChevronRight
                                        style={{ transitionTimingFunction: LP_V3_EASE }}
                                        className={cn(
                                            'size-5 text-foreground/50 transition-transform duration-300 motion-reduce:transition-none',
                                            solutionsOpen && 'rotate-90'
                                        )}
                                    />
                                </button>

                                <div
                                    style={{ transitionTimingFunction: LP_V3_EASE }}
                                    className={cn(
                                        'grid transition-[grid-template-rows] duration-300 motion-reduce:transition-none',
                                        solutionsOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                                    )}
                                >
                                    <div className="overflow-hidden">
                                        <div className="flex flex-col border-y border-border">
                                            {SOLUTIONS_GROUPS.map((group) => (
                                                <div
                                                    key={group.heading}
                                                    className="flex flex-col border-t border-border py-6 first:border-t-0"
                                                >
                                                    <span className="pb-2 text-[12px] font-normal uppercase leading-[1.2] text-[color:var(--lp-ash)]">
                                                        {group.heading}
                                                    </span>
                                                    {group.items.map((item) => (
                                                        <Link
                                                            key={item.label}
                                                            href={item.href}
                                                            prefetch={false}
                                                            onClick={close}
                                                            className="flex items-center gap-3 py-2 text-[16px] font-normal leading-[1.25] tracking-[-0.02em] text-foreground transition-colors hover:text-foreground/60"
                                                        >
                                                            {item.iconSrc && (
                                                                <Image
                                                                    src={item.iconSrc}
                                                                    alt=""
                                                                    width={18}
                                                                    height={18}
                                                                    className="size-[18px] shrink-0"
                                                                />
                                                            )}
                                                            {item.label}
                                                        </Link>
                                                    ))}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                {PRIMARY_NAV_LINKS.map((link) => (
                                    <Link
                                        key={link.label}
                                        href={link.href}
                                        prefetch={false}
                                        onClick={close}
                                        className="py-3 text-[24px] font-normal leading-[1.1] text-foreground"
                                    >
                                        {link.label}
                                    </Link>
                                ))}

                                <div className="mt-3 border-t border-border pt-4">
                                    <NavCtaButtons layout="stack" onNavigate={close} />
                                </div>
                            </nav>
                        </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Bar — always on top; logo stays put across states */}
            <div
                style={{ transitionTimingFunction: LP_V3_EASE }}
                className={cn(
                    'lp-v3-nav-chrome fixed inset-x-0 top-0 z-40 transition-colors duration-300 md:hidden',
                    open ? 'bg-background' : 'bg-gradient-to-b from-background via-background/60 to-transparent'
                )}
            >
                <div className={LP_GRID.contentSection}>
                    <div className={LP_GRID.content}>
                    <div className="flex items-center" style={{ height: LP_V3_NAV_HEIGHT }}>
                        <Link
                            href={UrlConfig['/'].getUrl()}
                            onClick={close}
                            aria-label="Superscale home"
                            className="shrink-0"
                        >
                            <Image
                                src="/assets/landing-v3/superscale-logo-with-text.svg"
                                alt="Superscale"
                                width={149}
                                height={32}
                                className="lp-nav-logo h-7 w-auto"
                                priority
                            />
                        </Link>

                        <div className="ml-auto flex items-center gap-2">
                            {/* Get started — fades out when open; keeps its slot so nothing shifts */}
                            <div
                                inert={open}
                                style={{ transitionTimingFunction: LP_V3_EASE }}
                                className={cn(
                                    'transition-opacity duration-200',
                                    open ? 'pointer-events-none opacity-0' : 'opacity-100'
                                )}
                            >
                                <NavCtaButtons layout="compact" />
                            </div>

                            {/* Toggle: ☰ morphs to ✕ */}
                            <button
                                type="button"
                                aria-label={open ? 'Close menu' : 'Open menu'}
                                aria-expanded={open}
                                onClick={() => setOpen((v) => !v)}
                                className="lp-nav-ink relative flex size-9 items-center justify-center rounded-[8px] text-foreground/80 transition-colors hover:bg-black/5 active:bg-black/10"
                            >
                                <Menu
                                    style={{ transitionTimingFunction: LP_V3_EASE }}
                                    className={cn(
                                        'size-5 transition-all duration-300 motion-reduce:transition-none',
                                        open ? 'rotate-90 scale-0 opacity-0' : 'rotate-0 scale-100 opacity-100'
                                    )}
                                />
                                <X
                                    style={{ transitionTimingFunction: LP_V3_EASE }}
                                    className={cn(
                                        'absolute left-1/2 top-1/2 size-5 -translate-x-1/2 -translate-y-1/2 transition-all duration-300 motion-reduce:transition-none',
                                        open ? 'rotate-0 scale-100 opacity-100' : '-rotate-90 scale-0 opacity-0'
                                    )}
                                />
                            </button>
                        </div>
                    </div>
                    </div>
                </div>
            </div>
        </>
    )
}
